import { FC, useState } from "react";
import PageContent from "../PageContent";
import {
  Button,
  Badge,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormDialog,
  FormDialogCancel,
  ConfirmationDialog,
  Input,
  Label,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@capacit-com/lara-law-shared-ui";
import {
  Plus,
  Pencil,
  Trash2,
  List,
  FileText,
  Link2,
  Bell,
  BellRing,
  Star,
  Clock,
  FileHeart,
} from "lucide-react";
import { toast } from "@capacit-com/lara-law-shared-ui";
import {
  useWidgetTemplates,
  useCreateWidgetTemplate,
  useUpdateWidgetTemplate,
  useDeleteWidgetTemplate,
} from "@/hooks/useDashboard";
import type {
  WidgetTemplate,
  WidgetTemplateConfig,
} from "@/models/dashboard/Dashboard";
import {
  WidgetType,
  getDefaultDynamicListConfig,
  getDefaultStaticListConfig,
  getDefaultShortcutConfig,
  getDefaultSubscriptionsConfig,
  getDefaultNotificationsConfig,
  getDefaultFavoriteSearchesConfig,
  getDefaultRecentSearchesConfig,
  getDefaultFavoriteDocumentsConfig,
} from "@/models/dashboard/WidgetConfig";
import { serializeWidgetTemplateConfig } from "@/models/dashboard/Dashboard";
import WidgetTemplateConfigDialog from "./components/WidgetTemplateConfigDialog";
import type { WidgetConfig } from "@/models/dashboard/WidgetConfig";
import { logger } from "@/lib/logger";

const WIDGET_TYPE_INFO: Record<
  WidgetType,
  { icon: typeof List; label: string }
> = {
  "dynamic-list": { icon: List, label: "Dynamisk liste" },
  "static-list": { icon: FileText, label: "Statisk liste" },
  shortcut: { icon: Link2, label: "Genveje" },
  subscriptions: { icon: Bell, label: "Abonnementer" },
  notifications: { icon: BellRing, label: "Notifikationer" },
  "favorite-searches": { icon: Star, label: "Favoritsøgninger" },
  "recent-searches": { icon: Clock, label: "Seneste søgninger" },
  "favorite-documents": { icon: FileHeart, label: "Favoritdokumenter" },
};

interface TemplateFormData {
  name: string;
  description: string;
  type: WidgetType;
  defaultName: string;
  defaultSize: { w: number; h: number };
}

const EMPTY_TEMPLATE_FORM: TemplateFormData = {
  name: "",
  description: "",
  type: "dynamic-list",
  defaultName: "",
  defaultSize: { w: 6, h: 4 },
};

const templateFormDataFor = (template: WidgetTemplate): TemplateFormData => ({
  name: template.name,
  description: template.description || "",
  type: template.config.type,
  defaultName: template.config.defaultName,
  defaultSize: template.config.defaultSize,
});

const WidgetTemplatesView: FC = () => {
  const { data: templates, isLoading } = useWidgetTemplates();
  const createTemplate = useCreateWidgetTemplate();
  const updateTemplate = useUpdateWidgetTemplate();
  const deleteTemplate = useDeleteWidgetTemplate();

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<WidgetTemplate | null>(
    null
  );
  const [deletingTemplateId, setDeletingTemplateId] = useState<string | null>(
    null
  );
  const [configuringTemplate, setConfiguringTemplate] =
    useState<WidgetTemplate | null>(null);

  const [formData, setFormData] =
    useState<TemplateFormData>(EMPTY_TEMPLATE_FORM);

  const resetForm = () => {
    setFormData(EMPTY_TEMPLATE_FORM);
  };

  // What the discard guard protects: the form compared with the template it
  // opened on (or the empty form for "Opret ny skabelon").
  const openedFormData: TemplateFormData = editingTemplate
    ? templateFormDataFor(editingTemplate)
    : EMPTY_TEMPLATE_FORM;
  const isTemplateFormDirty =
    JSON.stringify(formData) !== JSON.stringify(openedFormData);

  const handleCreateTemplate = async () => {
    if (!formData.name.trim() || !formData.defaultName.trim()) return;

    let widgetConfig;
    switch (formData.type) {
      case "dynamic-list":
        widgetConfig = getDefaultDynamicListConfig();
        break;
      case "static-list":
        widgetConfig = getDefaultStaticListConfig();
        break;
      case "shortcut":
        widgetConfig = getDefaultShortcutConfig();
        break;
      case "subscriptions":
        widgetConfig = getDefaultSubscriptionsConfig();
        break;
      case "notifications":
        widgetConfig = getDefaultNotificationsConfig();
        break;
      case "favorite-searches":
        widgetConfig = getDefaultFavoriteSearchesConfig();
        break;
      case "recent-searches":
        widgetConfig = getDefaultRecentSearchesConfig();
        break;
      case "favorite-documents":
        widgetConfig = getDefaultFavoriteDocumentsConfig();
        break;
    }

    const templateConfig: WidgetTemplateConfig = {
      type: formData.type,
      defaultName: formData.defaultName,
      defaultSize: formData.defaultSize,
      config: widgetConfig,
    };

    try {
      await createTemplate.mutateAsync({
        name: formData.name,
        description: formData.description || undefined,
        configJson: serializeWidgetTemplateConfig(templateConfig),
      });
      setShowCreateDialog(false);
      resetForm();
      toast.success("Skabelon oprettet", {
        description: `"${formData.name}" er nu tilgængelig for alle brugere.`,
      });
    } catch (e) {
      logger.error("Error creating template", {
        operation: "createTemplate",
        error: e,
        name: formData.name,
      });
      toast.error("Fejl", {
        description: "Der opstod en fejl ved oprettelse af skabelonen.",
      });
    }
  };

  const handleUpdateTemplateMetadata = async () => {
    if (
      !editingTemplate ||
      !formData.name.trim() ||
      !formData.defaultName.trim()
    )
      return;

    const templateConfig: WidgetTemplateConfig = {
      ...editingTemplate.config,
      type: formData.type,
      defaultName: formData.defaultName,
      defaultSize: formData.defaultSize,
    };

    try {
      await updateTemplate.mutateAsync({
        id: editingTemplate.id,
        request: {
          name: formData.name,
          description: formData.description || undefined,
          configJson: serializeWidgetTemplateConfig(templateConfig),
        },
      });
      setEditingTemplate(null);
      resetForm();
      toast.success("Skabelon opdateret");
    } catch (e) {
      logger.error("Error updating template", {
        operation: "updateTemplate",
        error: e,
        templateId: editingTemplate.id,
      });
      toast.error("Fejl", {
        description: "Der opstod en fejl ved opdatering af skabelonen.",
      });
    }
  };

  const handleConfigSave = async (
    defaultName: string,
    config: WidgetConfig
  ) => {
    if (!configuringTemplate) return;

    const templateConfig: WidgetTemplateConfig = {
      ...configuringTemplate.config,
      defaultName,
      config,
    };

    try {
      await updateTemplate.mutateAsync({
        id: configuringTemplate.id,
        request: {
          name: configuringTemplate.name,
          description: configuringTemplate.description || undefined,
          configJson: serializeWidgetTemplateConfig(templateConfig),
        },
      });
      toast.success("Widget-konfiguration gemt");
    } catch (e) {
      logger.error("Error saving template config", {
        operation: "saveTemplateConfig",
        error: e,
        templateId: configuringTemplate.id,
      });
      toast.error("Fejl", {
        description: "Der opstod en fejl ved gemning af konfigurationen.",
      });
    }
  };

  const handleDeleteTemplate = async () => {
    if (!deletingTemplateId) return;

    try {
      await deleteTemplate.mutateAsync(deletingTemplateId);
      setDeletingTemplateId(null);
      toast.success("Skabelon slettet");
    } catch (e) {
      logger.error("Error deleting template", {
        operation: "deleteTemplate",
        error: e,
        templateId: deletingTemplateId,
      });
      toast.error("Fejl", {
        description: "Der opstod en fejl ved sletning af skabelonen.",
      });
    }
  };

  const openEditDialog = (template: WidgetTemplate) => {
    setFormData(templateFormDataFor(template));
    setEditingTemplate(template);
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("da-DK", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <PageContent title="Widget-skabeloner">
      <div className="flex flex-col h-full p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold">Widget-skabeloner</h1>
            <p className="text-muted-foreground mt-1">
              Opret og administrer widget-skabeloner som brugere kan tilføje til
              deres dashboards.
            </p>
          </div>
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Opret skabelon
          </Button>
        </div>

        {/* Templates Table */}
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : !templates || templates.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground border rounded-lg">
            <List className="h-12 w-12 mb-4 opacity-50" />
            <p className="text-lg font-medium">Ingen skabeloner endnu</p>
            <p className="text-sm mt-1 mb-4">
              Opret din første widget-skabelon for at gøre den tilgængelig for
              brugere.
            </p>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Opret skabelon
            </Button>
          </div>
        ) : (
          // The page wrapper is h-full inside PageContent's own viewport-height
          // scroller, so this box has to scroll itself: with overflow-hidden every
          // row below the fold was clipped away with no scrollbar anywhere. Moving
          // the horizontal scrolling up here too (removeOverflowX) is what lets the
          // header row stick to this scrollport.
          <div className="themed-scrollbar border rounded-lg overflow-auto">
            <Table removeOverflowX={true}>
              <TableHeader className="sticky top-0 z-10 shadow-sm">
                <TableRow>
                  <TableHead>Navn</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Standardnavn</TableHead>
                  <TableHead>Oprettet</TableHead>
                  <TableHead className="w-[150px]">Handlinger</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map((template) => {
                  const TypeIcon = WIDGET_TYPE_INFO[template.config.type].icon;
                  return (
                    <TableRow key={template.id}>
                      <TableCell>
                        <div>
                          <span className="font-medium">{template.name}</span>
                          {template.description && (
                            <p className="text-sm text-muted-foreground mt-0.5">
                              {template.description}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="gap-1">
                          <TypeIcon className="h-3 w-3" />
                          {WIDGET_TYPE_INFO[template.config.type].label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {template.config.defaultName}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(template.createdAt)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setConfiguringTemplate(template)}
                            title="Konfigurer widget"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(template)}
                            title="Rediger metadata"
                          >
                            <FileText className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => setDeletingTemplateId(template.id)}
                            title="Slet skabelon"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Create/Edit Template Dialog */}
      <FormDialog
        open={showCreateDialog || editingTemplate !== null}
        onOpenChange={(open) => {
          if (!open) {
            setShowCreateDialog(false);
            setEditingTemplate(null);
            resetForm();
          }
        }}
        isDirty={isTemplateFormDirty}
      >
        <DialogHeader>
          <DialogTitle>
            {editingTemplate ? "Rediger skabelon" : "Opret ny skabelon"}
          </DialogTitle>
          <DialogDescription>
            {editingTemplate
              ? "Opdater metadata for denne widget-skabelon."
              : "Definer en ny widget-skabelon som brugere kan tilføje til deres dashboards."}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <div className="grid gap-2">
            <Label htmlFor="template-name">Skabelonnavn</Label>
            <Input
              id="template-name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder="F.eks. Seneste bekendtgørelser"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="template-description">Beskrivelse (valgfrit)</Label>
            <Textarea
              id="template-description"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder="Kort beskrivelse af hvad denne widget viser"
              rows={2}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="template-type">Widget-type</Label>
            <Select
              value={formData.type}
              onValueChange={(value: WidgetType) =>
                setFormData({ ...formData, type: value })
              }
              disabled={!!editingTemplate}
            >
              <SelectTrigger id="template-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="dynamic-list">Dynamisk liste</SelectItem>
                <SelectItem value="static-list">Statisk liste</SelectItem>
                <SelectItem value="shortcut">Genveje</SelectItem>
              </SelectContent>
            </Select>
            {editingTemplate && (
              <p className="text-xs text-muted-foreground">
                Widget-type kan ikke ændres efter oprettelse.
              </p>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="template-default-name">
              Standardnavn for widget
            </Label>
            <Input
              id="template-default-name"
              value={formData.defaultName}
              onChange={(e) =>
                setFormData({ ...formData, defaultName: e.target.value })
              }
              placeholder="F.eks. Bekendtgørelser"
            />
            <p className="text-xs text-muted-foreground">
              Dette navn bruges som standard når brugere tilføjer widgetten.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="template-width">Standardbredde</Label>
              <Input
                id="template-width"
                type="number"
                min={2}
                max={12}
                value={formData.defaultSize.w}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    defaultSize: {
                      ...formData.defaultSize,
                      w: parseInt(e.target.value) || 6,
                    },
                  })
                }
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="template-height">Standardhøjde</Label>
              <Input
                id="template-height"
                type="number"
                min={2}
                max={12}
                value={formData.defaultSize.h}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    defaultSize: {
                      ...formData.defaultSize,
                      h: parseInt(e.target.value) || 4,
                    },
                  })
                }
              />
            </div>
          </div>
        </DialogBody>

        <DialogFooter>
          <FormDialogCancel />
          <Button
            onClick={
              editingTemplate
                ? handleUpdateTemplateMetadata
                : handleCreateTemplate
            }
            disabled={
              !formData.name.trim() ||
              !formData.defaultName.trim() ||
              createTemplate.isPending ||
              updateTemplate.isPending
            }
          >
            {editingTemplate ? "Gem ændringer" : "Opret skabelon"}
          </Button>
        </DialogFooter>
      </FormDialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={deletingTemplateId !== null}
        onClose={() => setDeletingTemplateId(null)}
        onConfirm={handleDeleteTemplate}
        title="Slet skabelon?"
        description="Eksisterende widgets baseret på denne skabelon vil fortsat fungere, men nye widgets kan ikke oprettes fra den."
        confirmText="Slet skabelon"
        variant="destructive"
      />

      <WidgetTemplateConfigDialog
        template={configuringTemplate}
        onClose={() => setConfiguringTemplate(null)}
        onSave={handleConfigSave}
      />
    </PageContent>
  );
};

export default WidgetTemplatesView;
